import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, Hotel, Building2, Store, Zap, Settings, Leaf, Fuel } from "lucide-react";
import { useSite, tx } from "@/lib/lang";
import Nav from "@/components/site/Nav";
import { Reveal, SectionLabel } from "@/components/site/Reveal";
import SectionTabs from "@/components/site/SectionTabs";

const ICONS = { hotel: Hotel, building: Building2, store: Store, zap: Zap, settings: Settings, leaf: Leaf, fuel: Fuel };

const isAreaBlock = (b) => b.tipo === "blocco" || b.tipo === "blocco_sub";

function Points({ points, lang, light = false }) {
  const items = points?.[lang] || points?.it || [];
  return (
    <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
      {items.map((p, i) => (
        <li key={i} className={`flex items-start gap-3 text-sm ${light ? "text-white/85" : "text-ice/85"}`}>
          <Check size={16} className="mt-0.5 shrink-0 text-azure" />
          {typeof p === "string" ? (
            <span className="leading-relaxed">{p}</span>
          ) : (
            <span className="leading-relaxed">
              <strong className={`font-semibold ${light ? "text-white" : "text-ice"}`}>{p.t}</strong>
              {p.d ? <span className={light ? "text-white/70" : "text-muted-foreground"}> — {p.d}</span> : null}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/* Contenuto di approfondimento di un'area (blocco / blocco_sub), senza wrapper di sezione.
   Riusato sia dentro i tab (SectionTabs) sia dal rendering normale del blocco. */
function BloccoInner({ block, lang }) {
  return (
    <>
      <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-ice">{tx(block.title, lang)}</h3>
      {tx(block.sub, lang) && <p className="mt-3 font-serif italic text-lg text-azure leading-snug">{tx(block.sub, lang)}</p>}
      {tx(block.desc, lang) && <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-3xl">{tx(block.desc, lang)}</p>}
      {block.points && (
        <div className="mt-6">
          <Points points={block.points} lang={lang} />
        </div>
      )}
      {(block.subblocks || []).length > 0 && (
        <div className="mt-7 grid md:grid-cols-3 gap-4">
          {block.subblocks.map((sb, i) => (
            <div key={i} data-testid={`block-${block.num}-sub-${i}`} className="bg-onyx/40 border border-ice/8 rounded-xl p-5">
              <h4 className="font-mono text-xs font-semibold uppercase tracking-[0.12em] text-royal">{tx(sb.title, lang)}</h4>
              <ul className="mt-3 space-y-2.5">
                {(sb.points?.[lang] || sb.points?.it || []).map((p, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm text-ice/85">
                    <Check size={14} className="mt-0.5 text-azure shrink-0" />
                    <span className="leading-relaxed">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {tx(block.note, lang) && (
        <p className="mt-7 border-l-2 border-royal pl-4 text-sm italic text-muted-foreground max-w-3xl leading-relaxed">
          {tx(block.note, lang)}
        </p>
      )}
    </>
  );
}

/* Blocchi NON-area (cards, personas, steps, special, cta_finale) e fallback per blocco/blocco_sub
   quando renderizzati fuori dai tab. */
function Block({ block, index, lang, ctaPrimary, ctaSecondary }) {
  const tipo = block.tipo;

  if (tipo === "cards") {
    const cards = block.cards || [];
    const cols = cards.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3";
    return (
      <Reveal className="py-14 lg:py-16 border-t border-ice/8" dataTestId={`block-cards-${index}`}>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ice max-w-3xl">{tx(block.heading, lang)}</h2>
        {tx(block.sub, lang) && <p className="mt-3 text-base text-muted-foreground max-w-3xl">{tx(block.sub, lang)}</p>}
        {tx(block.desc, lang) && <p className="mt-3 text-base text-muted-foreground max-w-3xl">{tx(block.desc, lang)}</p>}
        <div className={`mt-8 grid sm:grid-cols-2 ${cols} gap-4`}>
          {cards.map((c, i) => {
            const Icon = ICONS[c.icon] || Zap;
            return (
              <div key={i} data-testid={`block-cards-${index}-card-${i}`} className="bg-surface border border-ice/8 rounded-2xl p-6 hover:border-royal/50 transition-colors">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-royal/10 border border-royal/20 text-royal">
                  <Icon size={20} />
                </span>
                <h3 className="mt-4 font-display text-base font-semibold text-ice leading-snug">{tx(c.title, lang)}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{tx(c.text, lang)}</p>
              </div>
            );
          })}
        </div>
      </Reveal>
    );
  }

  if (tipo === "blocco" || tipo === "blocco_sub") {
    const highlighted = block.highlight;
    return (
      <Reveal
        className={`py-14 lg:py-16 border-t border-ice/8 ${highlighted ? "lg:-mx-8 lg:px-8 bg-surface/60 lg:border lg:border-royal/25 lg:rounded-2xl lg:py-12" : ""}`}
        dataTestId={block.num ? `block-${block.num}` : `block-${index}`}
      >
        <div className="flex items-start gap-5">
          {block.num && <span className="font-mono text-sm font-semibold text-royal pt-2 shrink-0">{block.num}</span>}
          <div className="min-w-0">
            <BloccoInner block={block} lang={lang} />
          </div>
        </div>
      </Reveal>
    );
  }

  if (tipo === "personas") {
    return (
      <Reveal className="py-14 lg:py-16 border-t border-ice/8" dataTestId={`block-personas-${index}`}>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ice">{tx(block.heading, lang)}</h2>
        {tx(block.sub, lang) && <p className="mt-2 font-serif italic text-lg text-azure">{tx(block.sub, lang)}</p>}
        {tx(block.desc, lang) && <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-3xl">{tx(block.desc, lang)}</p>}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(block.personas || []).map((p, i) => (
            <div key={i} className="bg-carddark/60 border border-ice/8 rounded-xl p-5">
              <h3 className="font-display text-sm font-semibold text-royal uppercase tracking-wide">{tx(p.title, lang)}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{tx(p.text, lang)}</p>
            </div>
          ))}
        </div>
      </Reveal>
    );
  }

  if (tipo === "steps") {
    return (
      <Reveal className="py-14 lg:py-16 border-t border-ice/8" dataTestId={`block-steps-${index}`}>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ice">{tx(block.heading, lang)}</h2>
        {tx(block.sub, lang) && <p className="mt-2 font-serif italic text-lg text-azure">{tx(block.sub, lang)}</p>}
        <div className="mt-8 space-y-0 max-w-3xl">
          {(block.steps || []).map((s, i) => (
            <div key={i} className="flex gap-5 pb-8 last:pb-0 relative">
              {i < block.steps.length - 1 && <span className="absolute left-[15px] top-9 bottom-0 w-px bg-ice/10" />}
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-royal/15 border border-royal/30 text-royal font-mono text-xs font-semibold">
                {i + 1}
              </span>
              <div>
                <h3 className="font-display text-base font-semibold text-ice">{tx(s.title, lang)}</h3>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{tx(s.text, lang)}</p>
              </div>
            </div>
          ))}
        </div>
      </Reveal>
    );
  }

  if (tipo === "special") {
    return (
      <Reveal className="py-14 lg:py-16 border-t border-ice/8" dataTestId={`block-special-${index}`}>
        <div className="bg-ice text-white rounded-2xl p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-royal/30 blur-3xl" />
          {tx(block.badge, lang) && (
            <span className="inline-block font-mono text-xs font-semibold tracking-[0.2em] text-ice bg-royal px-4 py-1.5 rounded-full">
              {tx(block.badge, lang)}
            </span>
          )}
          <h2 className="mt-5 font-display text-2xl sm:text-3xl font-bold tracking-tight">{tx(block.heading, lang)}</h2>
          {tx(block.sub, lang) && <p className="mt-2 font-serif italic text-lg text-azure">{tx(block.sub, lang)}</p>}
          {tx(block.text, lang) &&
            tx(block.text, lang).split("\n\n").map((p, i) => (
              <p key={i} className="mt-4 text-sm sm:text-base text-white/75 leading-relaxed max-w-3xl">{p}</p>
            ))}
          {block.points && (
            <div className="mt-7">
              <Points points={block.points} lang={lang} light />
            </div>
          )}
        </div>
      </Reveal>
    );
  }

  if (tipo === "cta_finale") {
    return (
      <Reveal className="py-14 lg:py-16 border-t border-ice/8" dataTestId="cta-finale">
        <div className="bg-surface border border-royal/25 rounded-2xl p-8 lg:p-12">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ice max-w-2xl">{tx(block.heading, lang)}</h2>
          {tx(block.text, lang) && <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-3xl">{tx(block.text, lang)}</p>}
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              data-testid="target-cta"
              to="/#contatti"
              className="inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-full bg-royal hover:bg-azure hover:text-[#0B1424] text-white transition-colors"
            >
              {ctaPrimary}
              <ArrowRight size={15} />
            </Link>
            {ctaSecondary && (
              <Link
                data-testid="target-cta-secondary"
                to="/#contatti"
                className="inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-full border border-ice/15 text-ice hover:border-royal hover:text-royal transition-colors"
              >
                {ctaSecondary}
              </Link>
            )}
          </div>
        </div>
      </Reveal>
    );
  }

  return null;
}

export default function TargetPage() {
  const { id } = useParams();
  const { content, lang } = useSite();
  const segments = content.target || [];
  const seg = segments.find((s) => s.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!seg) {
    return (
      <div className="min-h-screen bg-onyx text-ice">
        <Nav />
        <div className="pt-40 pb-32 text-center">
          <p className="text-muted-foreground">{lang === "it" ? "Pagina non trovata." : "Page not found."}</p>
          <Link to="/" className="mt-4 inline-block text-azure hover:underline">
            ← {lang === "it" ? "Torna alla home" : "Back to home"}
          </Link>
        </div>
      </div>
    );
  }

  const pagina = seg.pagina || {};
  const blocks = pagina.blocks || [];
  const introParas = tx(seg.intro, lang).split("\n\n").filter(Boolean);
  const ctaPrimary = tx(pagina.cta_primary, lang);
  const ctaSecondary = tx(pagina.cta_secondary, lang);

  // Suddivisione blocchi: le "aree" (blocco/blocco_sub) diventano tab; il resto resta prima/dopo.
  const firstAreaIdx = blocks.findIndex(isAreaBlock);
  const areaBlocks = blocks.filter(isAreaBlock);
  const preBlocks = firstAreaIdx > 0 ? blocks.slice(0, firstAreaIdx) : [];
  const postBlocks = firstAreaIdx >= 0 ? blocks.slice(firstAreaIdx + 1).filter((b) => !isAreaBlock(b)) : blocks;

  const areaItems = areaBlocks.map((b, idx) => ({
    id: b.num || `area-${idx}`,
    badge: b.num || String(idx + 1).padStart(2, "0"),
    title: tx(b.title, lang),
    subtitle: tx(b.sub, lang),
    render: () => <BloccoInner block={b} lang={lang} />,
  }));

  return (
    <div className="min-h-screen bg-onyx text-ice">
      <Nav />

      <div className="relative h-[420px] lg:h-[520px] overflow-hidden" data-testid={`target-page-${id}`}>
        {seg.image && <img src={seg.image} alt={tx(seg.label, lang)} className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-onyx via-onyx/60 to-onyx/15" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 h-full flex flex-col justify-end pb-12">
          <Reveal>
            <span className="font-mono text-xs font-semibold tracking-[0.2em] text-royal uppercase">{tx(seg.label, lang)}</span>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-ice max-w-4xl">
              {tx(seg.headline, lang)}
            </h1>
            {tx(seg.payoff, lang) && <p className="mt-4 font-serif italic text-lg sm:text-xl text-azure max-w-2xl">{tx(seg.payoff, lang)}</p>}
          </Reveal>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-6 pb-10">
        <Reveal className="max-w-3xl pt-14 lg:pt-16">
          {introParas.map((p, i) => (
            <p key={i} className="text-base text-muted-foreground leading-relaxed mt-4 first:mt-0">{p}</p>
          ))}
          {(ctaPrimary || ctaSecondary) && (
            <div className="mt-8 flex flex-wrap gap-3">
              {ctaPrimary && (
                <Link
                  data-testid="target-cta-hero"
                  to="/#contatti"
                  className="inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-full bg-royal hover:bg-azure hover:text-[#0B1424] text-white transition-colors"
                >
                  {ctaPrimary}
                  <ArrowRight size={15} />
                </Link>
              )}
              {ctaSecondary && (
                <Link
                  data-testid="target-cta-hero-secondary"
                  to="/#contatti"
                  className="inline-flex items-center gap-2 text-sm font-semibold px-6 py-3 rounded-full border border-ice/15 text-ice hover:border-royal hover:text-royal transition-colors"
                >
                  {ctaSecondary}
                </Link>
              )}
            </div>
          )}
        </Reveal>

        {/* Blocchi introduttivi (es. griglia "cards") prima delle aree */}
        {preBlocks.map((b, i) => (
          <Block key={`pre-${i}`} block={b} index={i} lang={lang} ctaPrimary={ctaPrimary} ctaSecondary={ctaSecondary} />
        ))}

        {/* Overview delle aree: elenco a sinistra, approfondimento a destra (accordion su mobile) */}
        {areaItems.length > 0 && (
          <div className="pt-14 lg:pt-16 mt-14 lg:mt-16 border-t border-ice/8" data-testid="target-areas">
            <SectionLabel>{lang === "it" ? "Aree di intervento" : "Areas of work"}</SectionLabel>
            <h2 className="mt-4 mb-10 font-display text-2xl sm:text-3xl font-bold tracking-tight text-ice max-w-3xl">
              {lang === "it"
                ? "Le aree in cui Ethic può lavorare per te"
                : "The areas where Ethic can work for you"}
            </h2>
            <SectionTabs
              items={areaItems}
              ariaLabel={lang === "it" ? "Aree di intervento" : "Areas of work"}
              testIdPrefix="area"
            />
          </div>
        )}

        {/* Blocchi di chiusura (personas, steps, special, cta finale) dopo le aree */}
        {postBlocks.map((b, i) => (
          <Block key={`post-${i}`} block={b} index={firstAreaIdx + 1 + i} lang={lang} ctaPrimary={ctaPrimary} ctaSecondary={ctaSecondary} />
        ))}

        <div className="border-t border-ice/8 pt-10 pb-6 mt-14 lg:mt-16">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">
            {lang === "it" ? "Altri interlocutori" : "Other stakeholders"}
          </p>
          <div className="flex flex-wrap gap-2">
            {segments
              .filter((s) => s.id !== id)
              .map((s) => (
                <Link
                  key={s.id}
                  to={`/clienti/${s.id}`}
                  data-testid={`target-nav-${s.id}`}
                  className="px-4 py-2 rounded-full text-sm border border-ice/10 text-muted-foreground hover:text-ice hover:border-royal/50 transition-colors"
                >
                  {tx(s.label, lang)}
                </Link>
              ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-ice/5">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <p className="font-mono text-xs text-muted-foreground/60">
            © {new Date().getFullYear()} RR Solutions srl — ETHIC ENERGY · P.IVA 02582720997
          </p>
        </div>
      </footer>
    </div>
  );
}
